import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { LogOut, Home, ChevronRight, KeyRound, X, Globe } from "lucide-react";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import { usePortalLang } from "../../lib/PortalLanguageContext";
import { portalApi } from "../../lib/portalApi";
import ForcePasswordChangeModal from "./ForcePasswordChangeModal";
import { PreviewModeBanner } from "./PreviewAsControls";

const ROLE_LABEL = {
  admin: "Main Admin",
  project_manager: "Project Manager",
  site_engineer: "Site Engineer",
  client: "Client",
  consultant: "Consultant",
  subcontractor: "Subcontractor",
};

function LanguageSelector() {
  const { lang, setLang, langs } = usePortalLang();
  return (
    <div className="relative inline-flex items-center">
      <Globe size={13} className="text-[#6E1F2C] pointer-events-none absolute left-2.5" />
      <select
        data-testid="portal-language-selector"
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        aria-label="Select portal language"
        className="appearance-none pl-7 pr-3 py-2 text-[10px] tracking-[0.22em] uppercase bg-white text-[#6E1F2C] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] focus:outline-none focus:border-[#6E1F2C] cursor-pointer"
      >
        {langs.map((l) => (
          <option key={l.code} value={l.code}>{l.nativeLabel}</option>
        ))}
      </select>
    </div>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await portalApi.changePassword(current, next);
      setOk("Password updated. Use your new password the next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="portal-change-password-form"
        className="bg-white w-full max-w-[440px] border border-[#6E1F2C]/20 p-7 md:p-9"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Account</p>
            <h2 className="font-serif-display text-2xl text-[#1C110C]">Change password</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]">
            <X size={18} />
          </button>
        </div>
        {[
          { k: "current", label: "Current password", val: current, set: setCurrent, testid: "portal-change-pw-current" },
          { k: "next", label: "New password (min 8 chars)", val: next, set: setNext, testid: "portal-change-pw-new" },
          { k: "confirm", label: "Confirm new password", val: confirm, set: setConfirm, testid: "portal-change-pw-confirm" },
        ].map((f) => (
          <div key={f.k} className="mb-4">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">{f.label}</label>
            <input
              type="password"
              data-testid={f.testid}
              value={f.val}
              onChange={(e) => f.set(e.target.value)}
              required
              minLength={f.k === "current" ? undefined : 8}
              autoComplete={f.k === "current" ? "current-password" : "new-password"}
              className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
            />
          </div>
        ))}
        {err && <p data-testid="portal-change-pw-error" className="text-xs text-[#A3141A] mb-4">{err}</p>}
        {ok && <p data-testid="portal-change-pw-success" className="text-xs text-[#6E1F2C] mb-4">{ok}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="portal-change-pw-submit"
          className="w-full bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
        >
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

export default function PortalLayout({ children, breadcrumbs = [], title }) {
  const { user, loading, logout } = usePortalAuth();
  const { t, isRTL } = usePortalLang();
  const loc = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF5EC] text-[#6E1F2C]">
        Loading…
      </div>
    );
  }
  if (!user) {
    // Already on the login page — render the children (which is the login
    // page itself). Guards against a Navigate-replace loop when auth oscillates.
    if (loc.pathname === "/projects" || loc.pathname === "/projects/") {
      return <>{children}</>;
    }
    const next = loc.pathname + loc.search;
    // react-router's <Navigate> internally fires navigate() exactly once
    // per mount via a useRef+useEffect guard, so a single render here
    // cannot drive the production "100 replaceState() per 10s" loop on
    // its own. The loop required a partner route navigating back here;
    // the PortalLogin SAFE_LANDING fix breaks that cycle for unknown roles.
    return <Navigate to={`/projects?next=${encodeURIComponent(next)}`} replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAF5EC]" dir={isRTL ? "rtl" : "ltr"}>
      {user.impersonating && <PreviewModeBanner user={user} />}
      <header className="bg-white border-b border-[#6E1F2C]/15 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <Link
            to="/projects"
            data-testid="portal-brand"
            className="font-serif-display text-sm md:text-base tracking-[0.32em] text-[#6E1F2C] uppercase"
          >
            Time · Furniture
            <span className="ms-3 text-[10px] tracking-[0.36em] text-[#7A6B5F] uppercase border-l border-[#D4A963] ps-3">
              {t("nav.projects")}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="hidden sm:block text-right">
              <p className="text-[11px] text-[#1C110C] leading-tight">{user.name}</p>
              <p className="text-[9px] tracking-[0.28em] uppercase text-[#7A6B5F] leading-tight">
                {ROLE_LABEL[user.role] || user.role}
              </p>
            </div>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="portal-change-pw-open"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] hover:text-[#1C110C] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-2 transition-colors"
              aria-label={t("nav.changePassword")}
              title={t("nav.changePassword")}
            >
              <KeyRound size={13} /> <span className="hidden sm:inline">{t("nav.changePassword")}</span>
            </button>
            <button
              onClick={logout}
              data-testid="portal-logout"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] hover:text-[#1C110C] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-2 transition-colors"
              aria-label={t("nav.logout")}
            >
              <LogOut size={13} /> <span className="hidden sm:inline">{t("nav.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10">
        {breadcrumbs.length > 0 && (
          <nav data-testid="portal-breadcrumbs" className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-6 flex-wrap">
            <Link to="/projects" className="hover:text-[#6E1F2C] inline-flex items-center gap-1">
              <Home size={12} /> {t("nav.home", "Home")}
            </Link>
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={i}>
                <ChevronRight size={11} className="text-[#D4A963]" />
                {b.to ? (
                  <Link to={b.to} className="hover:text-[#6E1F2C]">{b.label}</Link>
                ) : (
                  <span className="text-[#1C110C]">{b.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        {title && (
          <h1 className="font-serif-display text-2xl md:text-4xl tracking-tight text-[#1C110C] mb-6 md:mb-8">
            {title}
          </h1>
        )}
        {children}
      </main>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      {user.force_password_change && <ForcePasswordChangeModal />}
    </div>
  );
}
